// Step 1: InfluencerAccountAuth for signup wizard
// Handles account creation with email, password, phone, first name, last name
// Advances to Step 2 after collecting account information

/*
QA Test Cases:
- Step 1 includes email, password, phone, first name, last name
- Refresh after Step 1 → values restore from "ih.wizardDraft.{uid}"
- Advances to Step 2 (Audience info) after successful validation
*/

import React, { useState, useEffect, useRef } from 'react';
import { validatePassword, validatePasswordMatch } from '../../../../utils/passwordValidation.js';
import { validatePhone } from '../../../../utils/phoneValidation.js';

// Constants for editable copy
const COPY = {
  title: "Account Information",
  subtitle: "Create your account",
  continueButton: "Continue",
  passwordNote: "Account created. You'll complete profile details below."
};

const InfluencerAccountAuth = ({ data, onDataChange, onNext, onNavigate, user }) => {
  // Initialize formData from data.account (if it exists) or from data directly (for backwards compatibility)
  const accountData = data?.account || data || {};
  
  const [formData, setFormData] = useState({
    email: accountData.email || '',
    password: accountData.password || '',
    confirmPassword: accountData.confirmPassword || '',
    firstName: accountData.firstName || '',
    lastName: accountData.lastName || '',
    phone: accountData.phone || ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [phoneError, setPhoneError] = useState('');

  // Sync formData when data prop changes (e.g., from localStorage restore)
  useEffect(() => {
    const accountData = data?.account || data || {};
    // Only update if we have new data and current formData is empty
    if (accountData.email && !formData.email) {
      setFormData({
        email: accountData.email || '',
        password: accountData.password || '',
        confirmPassword: accountData.confirmPassword || '',
        firstName: accountData.firstName || '',
        lastName: accountData.lastName || '',
        phone: accountData.phone || ''
      });
    }
  }, [data]); // Only depend on data, not formData to avoid loops

  // Update parent component when form data changes
  // Structure the data properly under 'account' key
  // Use a ref to track if we've already sent this data to prevent infinite loops
  const lastSentDataRef = useRef(null);
  
  useEffect(() => {
    const dataToSend = {
      account: {
        email: formData.email,
        password: formData.password,
        confirmPassword: formData.confirmPassword,
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone
      }
    };
    
    // Only send if data actually changed
    const dataString = JSON.stringify(dataToSend);
    if (lastSentDataRef.current !== dataString) {
      lastSentDataRef.current = dataString;
      onDataChange(dataToSend);
    }
  }, [formData.email, formData.password, formData.confirmPassword, formData.firstName, formData.lastName, formData.phone, onDataChange]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    console.log(`📝 Field changed: ${name} = "${value}"`);
    
    setFormData(prev => {
      const updated = {
        ...prev,
        [name]: value
      };
      console.log(`📝 Updated formData:`, updated);
      return updated;
    });
    
    // Clear phone error when user types in phone field
    if (name === 'phone') {
      setPhoneError('');
    }
    // Clear error when user starts typing
    if (error) setError('');
  };


  // Validation function
  const validate = async (stepData) => {
    const errors = [];
    
    // Always validate email and password (fresh start)
    if (!stepData.email?.trim()) {
      errors.push('Email is required');
    } else if (!/\S+@\S+\.\S+/.test(stepData.email)) {
      errors.push('Please enter a valid email address');
    }
    
    // Password validation using utility
    if (!stepData.password?.trim()) {
      errors.push('Password is required');
    } else {
      const passwordValidation = validatePassword(stepData.password);
      if (!passwordValidation.valid) {
        errors.push(...passwordValidation.errors);
      }
    }
    
    // Password match validation
    if (stepData.password) {
      const matchValidation = validatePasswordMatch(stepData.password, stepData.confirmPassword);
      if (!matchValidation.valid) {
        errors.push(matchValidation.error);
      }
    } else if (!stepData.confirmPassword?.trim()) {
      errors.push('Please confirm your password');
    }
    
    // Always validate these fields
    if (!stepData.firstName?.trim()) {
      errors.push('First name is required');
    } else if (stepData.firstName.length < 2) {
      errors.push('First name must be at least 2 characters');
    }

    if (!stepData.lastName?.trim()) {
      errors.push('Last name is required');
    } else if (stepData.lastName.length < 2) {
      errors.push('Last name must be at least 2 characters');
    }

    // Phone number validation
    if (!stepData.phone?.trim()) {
      errors.push('Phone number is required');
    } else {
      const phoneValidation = validatePhone(stepData.phone);
      if (!phoneValidation.valid) {
        errors.push(phoneValidation.error);
      }
    }

    return {
      valid: errors.length === 0,
      errors
    };
  };

  // Attach validate function to component
  InfluencerAccountAuth.validate = validate;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Debug: Log formData before validation with detailed breakdown
    console.log('🔍 Form submission - formData:', formData);
    console.log('🔍 Form submission - individual fields:', {
      email: formData.email,
      emailType: typeof formData.email,
      emailLength: formData.email?.length,
      emailTrimmed: formData.email?.trim(),
      password: formData.password ? '***' : 'empty',
      passwordType: typeof formData.password,
      passwordLength: formData.password?.length,
      confirmPassword: formData.confirmPassword ? '***' : 'empty',
      firstName: formData.firstName,
      firstNameType: typeof formData.firstName,
      firstNameLength: formData.firstName?.length,
      lastName: formData.lastName,
      lastNameType: typeof formData.lastName,
      phone: formData.phone,
      phoneType: typeof formData.phone,
      phoneLength: formData.phone?.length
    });
    
    // Validate form - check formData directly
    const validation = await validate(formData);
    console.log('🔍 Validation result:', validation);
    console.log('🔍 Validation errors detail:', validation.errors);
    console.log('🔍 Validation valid?', validation.valid);
    
    // Clear errors first
    setError('');
    setPhoneError('');
    
    if (!validation.valid) {
      console.log('❌ Validation failed:', validation.errors);
      console.log('❌ Field checks:', {
        hasEmail: !!formData.email?.trim(),
        hasPassword: !!formData.password?.trim(),
        hasConfirmPassword: !!formData.confirmPassword?.trim(),
        hasFirstName: !!formData.firstName?.trim(),
        hasLastName: !!formData.lastName?.trim(),
        hasPhone: !!formData.phone?.trim()
      });
      setError(validation.errors.join(', '));
      
      // Set phone error if phone validation failed
      if (formData.phone) {
        const phoneValidation = validatePhone(formData.phone);
        if (!phoneValidation.valid) {
          setPhoneError(phoneValidation.error);
        }
      } else if (!formData.phone?.trim()) {
        setPhoneError('Phone number is required');
      }
      
      return;
    }

    // Validation passed - ensure errors are cleared
    console.log('✅ Validation passed! Proceeding to next step...');
    setIsSubmitting(true);
    setError('');
    setPhoneError('');

    try {
      // Step 1: Clear localStorage role selection (this happens before auth now)
      localStorage.removeItem('ih.selectedRole');

      // Step 2: Update local wizard state with all Step 1 data (no Firebase operations)
      // Note: The useEffect above already handles updating the data structure,
      // but we ensure it's properly structured here as well
      const step1Data = {
        account: {
          email: formData.email,
          password: formData.password, // Store password temporarily for final auth step
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.phone
        }
      };
      
      console.log('💾 Saving step1Data to parent:', step1Data);
      onDataChange(step1Data);
      
      // Give React a moment to update state before calling onNext
      // This ensures the parent's validation sees the updated data
      await new Promise(resolve => setTimeout(resolve, 0));

      // Step 3: Advance to next step - no Firebase operations here
      console.log('➡️ Calling onNext() to advance to next step');
      onNext();

    } catch (error) {
      // This catch block is mostly for unexpected errors now, as auth is moved
      console.error('Account setup error:', error);
      setError('Failed to process step. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
        {/* Error Display */}
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
            {error}
            {error.includes('already has an account') && (
              <div className="mt-2">
                <button
                  type="button"
                  onClick={() => onNavigate('login')}
                  className="text-blue-600 hover:text-blue-500 underline text-sm"
                >
                  Sign in instead
                </button>
              </div>
            )}
          </div>
        )}

        {/* Account Information Section */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">{COPY.title}</h3>
          
          
          <div className="grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-8">
            {/* Email */}
            <div>
              <label htmlFor="email" className="form-label">
                Email Address *
              </label>
              <input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                className="form-input"
                placeholder="Enter your email"
                required
              />
            </div>

            {/* Phone Number */}
            <div>
              <label htmlFor="phone" className="form-label">
                Phone Number *
              </label>
              <input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                onBlur={(e) => {
                  // Validate on blur
                  const phoneValidation = validatePhone(e.target.value);
                  if (!phoneValidation.valid) {
                    setPhoneError(phoneValidation.error);
                  } else {
                    setPhoneError('');
                  }
                }}
                className={`form-input ${phoneError ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}`}
                placeholder="(555) 123-4567"
                required
              />
              {phoneError && (
                <p className="mt-1 text-xs text-red-500">
                  {phoneError}
                </p>
              )}
            </div>

            {/* First Name */}
            <div>
              <label htmlFor="firstName" className="form-label">
                First Name *
              </label>
              <input
                id="firstName"
                name="firstName"
                type="text"
                value={formData.firstName}
                onChange={handleChange}
                className="form-input"
                placeholder="Enter your first name"
                required
                minLength={2}
              />
            </div>

            {/* Last Name */}
            <div>
              <label htmlFor="lastName" className="form-label">
                Last Name *
              </label>
              <input
                id="lastName"
                name="lastName"
                type="text"
                value={formData.lastName}
                onChange={handleChange}
                className="form-input"
                placeholder="Enter your last name"
                required
                minLength={2}
              />
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="form-label">
                Password *
              </label>
              <input
                id="password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                className="form-input"
                placeholder="Create a password (min 9 characters, 3 of 4: lowercase, uppercase, number, special)"
                required
                minLength={9}
              />
            </div>

            {/* Confirm Password */}
            <div>
              <label htmlFor="confirmPassword" className="form-label">
                Confirm Password *
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange}
                className="form-input"
                placeholder="Confirm your password"
                required
              />
            </div>

          </div>
        </div>


        {/* Submit Button */}
        <div className="pt-4">
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Creating Account...' : COPY.continueButton}
          </button>
        </div>
    </form>
  );
};

export default InfluencerAccountAuth;